import styled from "styled-components";
import {
  Dropdown,
  Field,
  Link,
  Input,
  Option,
} from "@fluentui/react-components";
import {
  Add16Regular,
  Search16Regular,
  Checkmark16Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
} from "@fluentui/react-icons";
import i18next from "i18next";
import React, { KeyboardEvent, useContext, useMemo, useState } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";
import { SelectAllCheckbox } from "./SelectAllCheckbox";
import { Column, Grouping } from "./types";

type Bucket = { key: string; label: string; order: number; items: Column[] };

const FLAT_BUCKET = "__all__";

export const AvailableFieldsPane = () => {
  const {
    columns,
    groupings,
    onToggleColumnVisibility,
    onSetColumnsVisibility,
    strings,
    id,
  } = useContext(ColumnOptionsContext);

  const groupingList: Grouping[] = useMemo(
    () =>
      groupings && groupings.length > 0
        ? groupings
        : [
            {
              key: "alphabetical",
              label: strings.alphabeticalGroupingLabel,
              groupOf: () => null,
            },
          ],
    [groupings, strings.alphabeticalGroupingLabel]
  );

  const [groupByKey, setGroupByKey] = useState(groupingList[0]?.key ?? "");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const grouping =
    groupingList.find((g) => g.key === groupByKey) ?? groupingList[0];
  const term = search.trim().toLowerCase();

  const buckets = useMemo<Bucket[]>(() => {
    const available = columns.filter(
      (c) => c.isPresentInColumnOptions !== false
    );
    const matching = term
      ? available.filter((c) => (c.label || "").toLowerCase().includes(term))
      : available;
    const byKey = new Map<string, Bucket>();
    matching.forEach((column) => {
      const group = grouping?.groupOf(column) ?? null;
      const key = group ? group.key : FLAT_BUCKET;
      let bucket = byKey.get(key);
      if (!bucket) {
        bucket = {
          key,
          label: group?.label ?? "",
          order: group?.order ?? 50,
          items: [],
        };
        byKey.set(key, bucket);
      }
      bucket.items.push(column);
    });
    const collator = new Intl.Collator(i18next.language || undefined);
    const result = Array.from(byKey.values());
    result.forEach((b) =>
      b.items.sort((a, b2) => collator.compare(a.label || "", b2.label || ""))
    );
    return result.sort(
      (a, b) => a.order - b.order || collator.compare(a.label, b.label)
    );
  }, [columns, grouping, term]);

  const isFlat = buckets.length === 1 && buckets[0]?.key === FLAT_BUCKET;
  const expandedKey = (key: string) => `${grouping?.key}:${key}`;
  const isExpanded = (key: string) =>
    term ? true : !!expanded[expandedKey(key)];

  const toggleRow = (column: Column) => {
    if (column.isFixed) return;
    onToggleColumnVisibility(column.key);
  };

  const onRowKeyDown = (event: KeyboardEvent, column: Column) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleRow(column);
    }
  };

  const renderRow = (column: Column) => {
    const selected = !!column.isVisible;
    return (
      <Row
        key={column.key}
        role="button"
        tabIndex={column.isFixed ? -1 : 0}
        aria-pressed={selected}
        aria-disabled={column.isFixed || undefined}
        title={column.label}
        $selected={selected}
        $fixed={!!column.isFixed}
        onClick={() => toggleRow(column)}
        onKeyDown={(e) => onRowKeyDown(e, column)}
        data-testid={`column-options-available-${column.key}`}
        id={id ? `${id}-available-${column.key}` : undefined}
      >
        <RowIcon $selected={selected || !!column.isFixed}>
          {selected || column.isFixed ? (
            <Checkmark16Regular />
          ) : (
            <Add16Regular />
          )}
        </RowIcon>
        <RowLabel>{column.label}</RowLabel>
        {column.isFixed && <Required>{strings.requiredLabel}</Required>}
      </Row>
    );
  };

  return (
    <Wrapper>
      <Controls>
        <Field label={strings.groupByLabel}>
          <StyledDropdown
            value={grouping?.label ?? ""}
            selectedOptions={grouping ? [grouping.key] : []}
            onOptionSelect={(_, data) => {
              if (data.optionValue) setGroupByKey(data.optionValue);
            }}
            data-testid="column-options-groupby"
            id={id ? `${id}-group-by` : undefined}
          >
            {groupingList.map((g) => (
              <Option key={g.key} value={g.key}>
                {g.label}
              </Option>
            ))}
          </StyledDropdown>
        </Field>
        <StyledSearchBox
          type="search"
          contentBefore={<Search16Regular />}
          placeholder={strings.searchFieldsPlaceholder}
          aria-label={strings.searchFieldsPlaceholder}
          value={search}
          onChange={(_, data) => setSearch(data.value ?? "")}
          data-testid="column-options-search"
          id={id ? `${id}-search` : undefined}
        />
        <SelectAllCheckbox />
      </Controls>
      <ScrollArea id={id ? `${id}-columns-all` : undefined}>
        {buckets.length === 0 && <Empty>{strings.noResultsLabel}</Empty>}
        {isFlat && buckets[0]?.items.map(renderRow)}
        {!isFlat &&
          buckets.map((bucket) => {
            const selectedCount = bucket.items.filter((c) => c.isVisible).length;
            const allSelected = selectedCount === bucket.items.length;
            const open = isExpanded(bucket.key);
            const changeable = bucket.items
              .filter((c) => !c.isFixed && !!c.isVisible === allSelected)
              .map((c) => c.key);
            return (
              <Group
                key={bucket.key}
                data-testid={`column-options-group-${bucket.key}`}
              >
                <GroupHeader>
                  <GroupToggle
                    type="button"
                    aria-expanded={open}
                    // While searching, every matching group stays open.
                    disabled={!!term}
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [expandedKey(bucket.key)]: !prev[expandedKey(bucket.key)],
                      }))
                    }
                  >
                    {open ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                    <GroupLabel>{bucket.label}</GroupLabel>
                  </GroupToggle>
                  <GroupCount data-testid={`column-options-group-count-${bucket.key}`}>
                    {selectedCount} / {bucket.items.length}
                  </GroupCount>
                  <Link
                    as="button"
                    disabled={changeable.length === 0}
                    onClick={() => onSetColumnsVisibility(changeable, !allSelected)}
                    aria-label={`${
                      allSelected ? strings.removeAllLabel : strings.addAllLabel
                    }: ${bucket.label}`}
                    data-testid={`column-options-group-toggle-${bucket.key}`}
                  >
                    {allSelected ? strings.removeAllLabel : strings.addAllLabel}
                  </Link>
                </GroupHeader>
                {open && <GroupItems>{bucket.items.map(renderRow)}</GroupItems>}
              </Group>
            );
          })}
      </ScrollArea>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  min-width: 0;
  min-height: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding-bottom: 8px;
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 180px;
`;

const StyledSearchBox = styled(Input)`
  flex: 1;
  max-width: none;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
`;

const Empty = styled.div`
  padding: 16px 8px;
  color: ${(props) => props.theme.palette.neutralSecondary};
`;

const Group = styled.div`
  margin-bottom: 6px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 8px 2px 0;
  border-radius: 4px;
  background-color: ${(props) => props.theme.palette.neutralLighter};
`;

const GroupToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
`;

const GroupLabel = styled.span`
  font-weight: 600;
`;

const GroupCount = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.palette.neutralSecondary};
`;

const GroupItems = styled.div`
  padding: 4px 0 4px 12px;
`;

const Row = styled.div<{ $selected: boolean; $fixed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: ${(props) => (props.$fixed ? "default" : "pointer")};
  opacity: ${(props) => (props.$fixed ? 0.7 : 1)};
  background-color: ${(props) =>
    props.$selected ? props.theme.palette.themeLighterAlt : "transparent"};

  &:hover {
    background-color: ${(props) =>
      props.$fixed
        ? props.$selected
          ? props.theme.palette.themeLighterAlt
          : "transparent"
        : props.theme.palette.neutralLighter};
  }
`;

const RowIcon = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  color: ${(props) =>
    props.$selected
      ? props.theme.palette.themePrimary
      : props.theme.palette.neutralSecondary};
`;

const RowLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Required = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.palette.neutralSecondary};
`;
